import { useCallback } from 'react'
import { useTheme } from '@dhl-ui/components'
import { useNativeBridge, type ShowNativeBackdropArgs } from '@dhl/native-bridge'

type UseNativeDialogBackdropResult = {
  handleDialogClosing: () => void
  handleDialogOpen: () => void
}

type CustomShowNativeBackdropArgs = Omit<ShowNativeBackdropArgs, 'color'>

export const useThemedNativeBackdrop = (): {
  hideNativeBackdrop: () => void
  showNativeBackdrop: (args?: CustomShowNativeBackdropArgs) => void
} => {
  const { hideNativeBackdrop: hideBackdrop, showNativeBackdrop: showBackdrop } = useNativeBridge()
  const {
    palette: {
      background: { paper },
      components: {
        backdrop: { fill: backdropColor },
      },
    },
  } = useTheme()

  const showNativeBackdrop = useCallback(
    (args: CustomShowNativeBackdropArgs = {}) => {
      showBackdrop({
        color: backdropColor,
        bottomColor: paper,
        visibleOnFooter: false,
        transition: 'fade',
        ...args,
      })
    },
    [showBackdrop, backdropColor, paper]
  )

  return { hideNativeBackdrop: hideBackdrop, showNativeBackdrop }
}

export const useNativeDialogBackdrop = (): UseNativeDialogBackdropResult => {
  const { hideNativeBackdrop, showNativeBackdrop } = useThemedNativeBackdrop()

  const handleDialogClosing = useCallback(hideNativeBackdrop, [hideNativeBackdrop])

  const handleDialogOpen = useCallback(
    () => showNativeBackdrop({ timeout: 200, transition: 'fade', visibleOnFooter: false }),
    [showNativeBackdrop]
  )

  return {
    handleDialogClosing,
    handleDialogOpen,
  }
}
